import React, { useState, useEffect } from 'react';
import ConsolBadge from './ConsolBadge';
import {
  Button,
  Grid,
  GridItem,
  Text,
  Badge,
  VStack,
  HStack,
  Box,
  Separator,
  Flex,
  Dialog,
  Popover,
  Portal,
  Spinner,
  ScrollArea,
  Icon,
} from '@chakra-ui/react';
import { FiMapPin, FiUser, FiMail, FiPhone } from 'react-icons/fi';
import { LuContainer, LuPackage, LuPlane, LuWrench, LuCheck, LuX } from 'react-icons/lu';
import axios from 'axios';
import AgentComments from './AgentComments';
import { config } from '../config';

const API_URL = config.BACKEND_URL;

const ViewAgentDetailsModal = ({ agent, onClose, formatDate, dataType = 'main' }) => {
  const [cityPorts, setCityPorts] = useState([]);
  const [allCityPorts, setAllCityPorts] = useState([]);
  const [loadingCityPorts, setLoadingCityPorts] = useState(false);
  const [selectedCityPort, setSelectedCityPort] = useState(null);
  const [specialServicesModalOpen, setSpecialServicesModalOpen] = useState(false);
  const [countryCode, setCountryCode] = useState('N/A');

  // Fetch all city ports with PIC info
  useEffect(() => {
    const fetchAllCityPorts = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/city-ports`, {
          withCredentials: true
        });
        setAllCityPorts(response.data);
      } catch (error) {
        console.error('Error fetching city ports:', error);
      }
    };
    fetchAllCityPorts();
  }, []);

  // Fetch city ports assigned to this agent (or pending upload)
  useEffect(() => {
    const fetchAgentCityPorts = async () => {
      if (!agent?.id) return;
      setLoadingCityPorts(true);
      try {
        // Check if this is a pending upload (has approval_status field)
        const isPendingUpload = agent.approval_status !== undefined;
        const endpoint = isPendingUpload
          ? (dataType === 'branch'
            ? `${API_URL}/api/city-ports/branch-pending/${agent.id}`
            : `${API_URL}/api/city-ports/pending/${agent.id}`)
          : (dataType === 'branch'
            ? `${API_URL}/api/city-ports/branch-agent/${agent.id}`
            : `${API_URL}/api/city-ports/agent/${agent.id}`);

        const response = await axios.get(endpoint, {
          withCredentials: true
        });
        setCityPorts(response.data);
      } catch (error) {
        console.error('Error fetching city ports:', error);
        // For pending uploads, table may not exist yet - just set empty
        setCityPorts([]);
      } finally {
        setLoadingCityPorts(false);
      }
    };
    fetchAgentCityPorts();
  }, [agent?.id, agent?.approval_status, dataType]);

  useEffect(() => {
    const fetchCountryCode = async () => {
      try {
        if (!agent?.country) {
          setCountryCode('N/A');
          return;
        }

        const response = await axios.get(`${API_URL}/api/countries`, {
          withCredentials: true,
          params: { activeOnly: false }
        });

        const countries = Array.isArray(response.data) ? response.data : [];
        const normalizedAgentCountry = agent.country.toString().trim().toUpperCase();
        const match = countries.find((c) => (c.name || '').toString().trim().toUpperCase() === normalizedAgentCountry);
        setCountryCode(match?.code ? match.code.toString().trim().toUpperCase() : 'N/A');
      } catch (error) {
        console.error('Error fetching country code:', error);
        setCountryCode('N/A');
      }
    };

    fetchCountryCode();
  }, [agent?.country]);

  // Find PIC info for a city port name (for legacy text data)
  const findCityPortPIC = (cityPortName) => {
    const normalizedName = cityPortName.toLowerCase().trim();
    return allCityPorts.find(cp =>
      cp.name.toLowerCase().trim() === normalizedName ||
      cp.name.toLowerCase().includes(normalizedName) ||
      normalizedName.includes(cp.name.toLowerCase())
    );
  };

  const isFieldAvailable = (value) => {
    return value && value.trim() !== '' && value !== '-';
  };

  const getCityPortList = (value) => {
    if (!value) return [];
    return value
      .split(/[,\n]/)
      .map(item => item.trim())
      .filter(item => item.length > 0);
  };

  const formatMembershipDate = (dateValue) => {
    if (!dateValue) return 'Not Available';
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return 'Invalid Date';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const getStatusBadge = (status) => {
    if (!status || status.toString().trim() === '') return <Badge colorPalette="gray">N/A</Badge>;
    const normalizedStatus = status.toString().trim().toUpperCase();
    const colorPalette = normalizedStatus === 'APPROVED' ? 'green' : normalizedStatus === 'POTENTIAL' ? 'yellow' : 'gray';
    return <Badge colorPalette={colorPalette}>{normalizedStatus}</Badge>;
  };

  const getContractBadge = (contract) => {
    if (!contract || contract.toString().trim() === '') return <Badge colorPalette="gray">N/A</Badge>;
    const normalized = contract.toString().trim().toUpperCase();
    const colorPalette = ['YES', 'TRUE'].includes(normalized) ? 'green' : 'red';
    return <Badge colorPalette={colorPalette}>{normalized}</Badge>;
  };

  const getAdditionalBadge = (additional) => {
    if (!additional || additional.toString().trim() === '') return <Badge colorPalette="gray">N/A</Badge>;
    const normalized = additional.toString().trim().toUpperCase();
    let colorPalette = 'gray';
    if (normalized.includes('AGENT')) colorPalette = 'green';
    else if (normalized.includes('QTM')) colorPalette = 'blue';
    else if (normalized.includes('BOTH')) colorPalette = 'purple';
    return <Badge colorPalette={colorPalette}>{normalized}</Badge>;
  };

  const getServiceAvailability = (serviceValue) => {
    const normalizedValue = serviceValue ? serviceValue.toString().trim().toUpperCase() : '';

    return normalizedValue &&
      normalizedValue !== '' &&
      normalizedValue !== '-' &&
      normalizedValue !== 'NULL' &&
      (['YES', 'AVAILABLE', 'TRUE', 'Y', 'X'].includes(normalizedValue) ||
        normalizedValue.includes('FCL') || normalizedValue.includes('LCL') || normalizedValue.includes('AIR') ||
        normalizedValue.includes('FREIGHT') || normalizedValue.includes('SEA') || normalizedValue.includes('WAREHOUSING'));
  };

  const getCompactServiceBadge = (serviceName, serviceValue, icon, isStrongest = false) => {
    const isAvailable = getServiceAvailability(serviceValue);
    const colorPalette = isAvailable ? 'green' : 'red';

    return (
      <Badge
        colorPalette={colorPalette}
        variant={isAvailable ? 'solid' : 'subtle'}
        border={isAvailable ? undefined : 'none'}
        px={2} py={1} borderRadius="md"
        display="flex" alignItems="center" gap={1}
        title={`${serviceName}: ${isAvailable ? 'Available' : 'Not Available'}${isStrongest ? ' (Strongest Service)' : ''}`}
        boxShadow={isStrongest && isAvailable ? '0 0 10px 3px rgba(234, 179, 8, 0.8), inset 0 0 5px rgba(234, 179, 8, 0.3)' : 'none'}
        outline={isStrongest && isAvailable ? '2px solid' : 'none'}
        outlineColor={isStrongest && isAvailable ? 'yellow.400' : 'transparent'}
        outlineOffset={isStrongest && isAvailable ? '1px' : '0'}
      >
        <Icon as={icon} boxSize="14px" />
        <Text>{serviceName}</Text>
        {isAvailable ? <Icon as={LuCheck} boxSize="14px" /> : <Icon as={LuX} boxSize="14px" />}
        {isStrongest && isAvailable && <Text>⭐</Text>}
      </Badge>
    );
  };

  if (!agent) return null;

  // Helper to safely get nested properties or default
  // Also handles objects - converts them to string or extracts a display value
  const getVal = (val, fallback = '-') => {
    if (val === null || val === undefined) return fallback;
    if (typeof val === 'object') {
      // If it's an object, try to get a display name property
      if (val.name) return val.name;
      if (val.displayName) return val.displayName;
      if (val.email) return val.email;
      // Otherwise convert to string representation
      return JSON.stringify(val);
    }
    return val ? String(val) : fallback;
  };

  // Helper to split string lists (e.g. "FCL; LCL")
  const getList = (str) => {
    if (!str) return [];
    return str.split(/[;,]/).map((s) => s.trim()).filter((s) => s);
  };

  const specialServices = getList(agent.special_service);
  const specialServiceCount = specialServices.length;

  const getYesNoColorScheme = (value) => {
    if (!value || value.toString().trim() === '') return 'gray';
    const normalized = value.toString().trim().toUpperCase();
    if (['YES', 'TRUE', 'Y', '1'].includes(normalized)) return 'green';
    if (['NO', 'FALSE', 'N', '0'].includes(normalized)) return 'red';
    return 'blue';
  };

  const getStatusColorScheme = (value) => {
    if (!value || value.toString().trim() === '') return 'gray';
    const normalized = value.toString().trim().toUpperCase();
    if (normalized === 'APPROVED') return 'green';
    if (normalized === 'POTENTIAL') return 'yellow';
    if (normalized === 'REJECTED') return 'red';
    return 'blue';
  };

  const getServiceColorScheme = (serviceValue) => {
    if (!serviceValue || serviceValue.toString().trim() === '') return 'gray';
    const normalized = serviceValue.toString().trim().toUpperCase();
    if (normalized === '-' || normalized === 'NULL') return 'gray';
    if (['YES', 'TRUE', 'Y', '1', 'AVAILABLE', 'X'].includes(normalized)) return 'green';
    if (['NO', 'FALSE', 'N', '0'].includes(normalized)) return 'red';
    return 'blue';
  };

  const lastEditedBy = getVal(agent.last_edited_by || agent.updated_by || agent.edited_by || agent.editor);
  const lastEditedTime = agent.last_edited_time ? formatDate(agent.last_edited_time) : formatDate(agent.updated_at);

  return (
    <>
      <Dialog.Root open={true} onOpenChange={(e) => !e.open && onClose()}>
        <Dialog.Backdrop bg="blackAlpha.600" backdropFilter="blur(5px)" />
        <Dialog.Positioner>
          <Dialog.Content maxW="90vw" borderRadius="xl">
            <Dialog.Header bg="purple.50" borderTopRadius="xl" borderBottom="1px" borderColor="gray.100" py={4} px={6} position="relative">
              <Flex justify="space-between" align="flex-start" pr={20}>
                <HStack spacing={3} align="center" flexWrap="wrap">
                  <Text fontSize={{ base: 'lg', md: 'xl' }} fontWeight="bold" color="purple.800">
                    {getVal(agent.agent_name)}
                  </Text>
                  <Badge colorPalette="purple" variant="solid" borderRadius="full" px={3} py={1}>
                    {getVal(agent.country)}
                  </Badge>
                </HStack>
              </Flex>
              <Text fontSize="xs" color="gray.600" position="absolute" top={4} right={6} textAlign="right">
                ID: {getVal(agent.id)}
              </Text>
            </Dialog.Header>
            <Dialog.CloseTrigger onClick={onClose} />

            <Dialog.Body py={6} px={{ base: 4, md: 8 }}>
              <Grid templateColumns={{ base: '1fr', xl: '2.5fr 1fr' }} gap={6} alignItems="start">
                <GridItem>
                  <VStack align="stretch" spacing={6}>
                    <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={6}>
                      <GridItem>
                        <Box border="1px" borderColor="gray.200" borderRadius="lg" p={5} bg="white">
                          <Text fontSize="sm" fontWeight="bold" color="gray.700" mb={3}>Status Overview</Text>
                          <HStack spacing={3} wrap="wrap">
                            <Badge colorPalette={getStatusColorScheme(agent.status)} borderRadius="full" px={3} py={1}>
                              Status: {getVal(agent.status)}
                            </Badge>
                            <Badge colorPalette={getYesNoColorScheme(agent.contract)} borderRadius="full" px={3} py={1}>
                              Contract: {getVal(agent.contract)}
                            </Badge>
                            <Badge colorPalette="blue" borderRadius="full" px={3} py={1}>
                              Additional: {getVal(agent.additional)}
                            </Badge>
                          </HStack>
                        </Box>
                      </GridItem>

                      <GridItem>
                        <Box border="1px" borderColor="gray.200" borderRadius="lg" p={5} bg="white">
                          <Text fontSize="sm" fontWeight="bold" color="gray.700" mb={3}>Services</Text>
                          {(() => {
                            const strongestList = getList(agent.strongest_service).map((s) => s.toUpperCase());
                            const services = [
                              { name: 'FCL', value: agent.fcl, icon: LuContainer, isStrongest: strongestList.includes('FCL') },
                              { name: 'LCL', value: agent.lcl, icon: LuPackage, isStrongest: strongestList.includes('LCL') },
                              { name: 'AIR', value: agent.air, icon: LuPlane, isStrongest: strongestList.includes('AIR') }
                            ];

                            const availableServices = services.filter((service) => getServiceAvailability(service.value));
                            const unavailableServices = services.filter((service) => !getServiceAvailability(service.value));

                            return (
                              <VStack align="stretch" spacing={2}>
                                <HStack wrap="wrap" spacing={2}>
                                  {availableServices.map((service) => (
                                    <React.Fragment key={service.name}>
                                      {getCompactServiceBadge(service.name, service.value, service.icon, service.isStrongest)}
                                    </React.Fragment>
                                  ))}
                                  <Badge
                                    colorPalette={specialServiceCount > 0 ? 'purple' : 'gray'}
                                    variant="subtle"
                                    px={2}
                                    py={1}
                                    borderRadius="full"
                                    fontSize="xs"
                                    cursor="pointer"
                                    onClick={() => setSpecialServicesModalOpen(true)}
                                    _hover={{ opacity: 0.8 }}
                                    title={specialServiceCount > 0 ? `Special Services (${specialServiceCount}): Click to view details` : 'Special Service: Not Available'}
                                  >
                                    <Icon as={LuWrench} boxSize="14px" /> Special {specialServiceCount > 0 ? `(${specialServiceCount})` : ''}
                                  </Badge>
                                  <ConsolBadge
                                    consolExport={agent.consol_export}
                                    consolImport={agent.consol_import}
                                    consolOversea={agent.consol_oversea}
                                  />
                                </HStack>
                                {unavailableServices.length > 0 && (
                                  <HStack wrap="wrap" spacing={2}>
                                    {unavailableServices.map((service) => (
                                      <React.Fragment key={service.name}>
                                        {getCompactServiceBadge(service.name, service.value, service.icon, service.isStrongest)}
                                      </React.Fragment>
                                    ))}
                                  </HStack>
                                )}
                              </VStack>
                            );
                          })()}

                          <Box mt={4}>
                            <Text fontSize="xs" color="gray.500" mb={2}>Special Services</Text>
                            {specialServices.length > 0 ? (
                              <Flex wrap="wrap" gap={2}>
                                {specialServices.map((service, idx) => (
                                  <Badge key={idx} colorPalette="purple" borderRadius="full" variant="subtle" px={3} py={1}>
                                    {service}
                                  </Badge>
                                ))}
                              </Flex>
                            ) : (
                              <Text fontSize="sm" color="gray.500">-</Text>
                            )}
                          </Box>
                        </Box>
                      </GridItem>
                    </Grid>

                    <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={6}>
                      <GridItem>
                        <Box border="1px" borderColor="gray.200" borderRadius="lg" p={5} bg="white">
                          <Text fontSize="sm" fontWeight="bold" color="gray.700" mb={3}>Contact Information</Text>
                          <Grid templateColumns={{ base: '140px 1fr', md: '160px 1fr' }} gap={2}>
                            <Text fontSize="sm" color="gray.600">PIC Overseas</Text>
                            <Text fontSize="sm" color="gray.800">{getVal(agent.pic_overseas)}</Text>

                            <Text fontSize="sm" color="gray.600">Phone Number</Text>
                            <Text fontSize="sm" color="gray.800">{getVal(agent.phone_number)}</Text>

                            <Text fontSize="sm" color="gray.600">Email PIC</Text>
                            <Text fontSize="sm" color="blue.600" wordBreak="break-word">{getVal(agent.email_pic)}</Text>

                            <Text fontSize="sm" color="gray.600">Short Name</Text>
                            <Text fontSize="sm" color="gray.800">
                              {agent.short_name ? `${getVal(agent.short_name)} - ${countryCode || 'N/A'}` : '-'}
                            </Text>

                            <Text fontSize="sm" color="gray.600">MBL Information</Text>
                            <Text fontSize="sm" color="gray.800" whiteSpace="pre-wrap">{getVal(agent.mbl_information)}</Text>

                            <Text fontSize="sm" color="gray.600">Remark</Text>
                            <Text fontSize="sm" color="gray.800" whiteSpace="pre-wrap">{getVal(agent.remark)}</Text>
                          </Grid>
                        </Box>
                      </GridItem>

                      <GridItem>
                        <Box border="1px" borderColor="gray.200" borderRadius="lg" p={5} bg="white">
                          <Text fontSize="sm" fontWeight="bold" color="gray.700" mb={3}>Contact Manager</Text>
                          <Grid templateColumns={{ base: '140px 1fr', md: '160px 1fr' }} gap={2}>
                            <Text fontSize="sm" color="gray.600">Manager</Text>
                            <Text fontSize="sm" color="gray.800">{getVal(agent.manager)}</Text>

                            <Text fontSize="sm" color="gray.600">Email Manager</Text>
                            <Text fontSize="sm" color="blue.600" wordBreak="break-word">{getVal(agent.email_manager)}</Text>

                            <Text fontSize="sm" color="gray.600">Phone Manager</Text>
                            <Text fontSize="sm" color="gray.800">{getVal(agent.phone_manager)}</Text>
                          </Grid>
                        </Box>
                      </GridItem>
                    </Grid>

                    <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={6}>
                      <GridItem>
                        <Box border="1px" borderColor="gray.200" borderRadius="lg" p={5} bg="white">
                          <Text fontSize="sm" fontWeight="bold" color="gray.700" mb={3}>Business Information</Text>
                          <Grid templateColumns={{ base: '140px 1fr', md: '160px 1fr' }} gap={2}>
                            <Text fontSize="sm" color="gray.600">Network</Text>
                            <Text fontSize="sm" color="gray.800">{getVal(agent.network)}</Text>

                            <Text fontSize="sm" color="gray.600">City/Port</Text>
                            <Box>
                              {loadingCityPorts ? (
                                <Spinner size="sm" color="purple.500" />
                              ) : cityPorts.length > 0 ? (
                                /* Show assigned city ports from DB */
                                <Flex wrap="wrap" gap={2}>
                                  {cityPorts.map((cp) => (
                                    <Popover.Root key={cp.id} positioning={{ placement: 'top' }}>
                                      <Popover.Trigger asChild>
                                        <Badge
                                          colorPalette="purple"
                                          variant="subtle"
                                          borderRadius="full"
                                          px={3}
                                          py={1}
                                          cursor="pointer"
                                          _hover={{ bg: 'purple.100', transform: 'scale(1.05)' }}
                                          transition="all 0.2s"
                                          display="flex"
                                          alignItems="center"
                                          gap={1}
                                        >
                                          <FiMapPin size={12} />
                                          {cp.name}
                                        </Badge>
                                      </Popover.Trigger>
                                      <Portal>
                                        <Popover.Positioner>
                                          <Popover.Content bg="white" shadow="xl" borderRadius="lg" p={4} minW="250px">
                                            <Popover.Arrow>
                                              <Popover.ArrowTip />
                                            </Popover.Arrow>
                                            <VStack align="stretch" spacing={3}>
                                              <Text fontWeight="bold" color="purple.700" fontSize="md">
                                                <FiMapPin style={{ display: 'inline', marginRight: '8px' }} />
                                                {cp.name}
                                              </Text>
                                              <Separator />
                                              <Text fontSize="sm" fontWeight="semibold" color="gray.600">PIC Information</Text>
                                              {(cp.pics && cp.pics.length > 0) ? (
                                                <ScrollArea.Root maxH="200px" type="scroll">
                                                  <ScrollArea.Viewport>
                                                    <ScrollArea.Content>
                                                      <VStack align="stretch" spacing={3} pr={3}>
                                                        {cp.pics.map((pic, idx) => (
                                                          <Box key={idx} p={2} bg="gray.50" borderRadius="md" border="1px solid" borderColor="gray.100">
                                                            <Text fontSize="sm" fontWeight="bold" color="purple.600" mb={1}>{pic.pic_name}</Text>
                                                            <VStack align="start" spacing={1}>
                                                              {pic.pic_email && (
                                                                <HStack>
                                                                  <FiMail size={12} color="gray" />
                                                                  <Text fontSize="xs" color="blue.600">{pic.pic_email}</Text>
                                                                </HStack>
                                                              )}
                                                              {pic.pic_phone && (
                                                                <HStack>
                                                                  <FiPhone size={12} color="gray" />
                                                                  <Text fontSize="xs">{pic.pic_phone}</Text>
                                                                </HStack>
                                                              )}
                                                            </VStack>
                                                          </Box>
                                                        ))}
                                                      </VStack>
                                                    </ScrollArea.Content>
                                                  </ScrollArea.Viewport>
                                                  <ScrollArea.Scrollbar>
                                                    <ScrollArea.Thumb />
                                                  </ScrollArea.Scrollbar>
                                                </ScrollArea.Root>
                                              ) : (cp.pic_name || cp.pic_email || cp.pic_phone) ? (
                                                /* Legacy Single PIC Fallback */
                                                <VStack align="stretch" spacing={2} p={2} bg="gray.50" borderRadius="md">
                                                  {cp.pic_name && (
                                                    <HStack>
                                                      <FiUser color="gray" />
                                                      <Text fontSize="sm">{cp.pic_name}</Text>
                                                    </HStack>
                                                  )}
                                                  {cp.pic_email && (
                                                    <HStack>
                                                      <FiMail color="gray" />
                                                      <Text fontSize="sm" color="blue.600">{cp.pic_email}</Text>
                                                    </HStack>
                                                  )}
                                                  {cp.pic_phone && (
                                                    <HStack>
                                                      <FiPhone color="gray" />
                                                      <Text fontSize="sm">{cp.pic_phone}</Text>
                                                    </HStack>
                                                  )}
                                                </VStack>
                                              ) : (
                                                <Text fontSize="sm" color="gray.400" fontStyle="italic">No PIC assigned</Text>
                                              )}
                                            </VStack>
                                          </Popover.Content>
                                        </Popover.Positioner>
                                      </Portal>
                                    </Popover.Root>
                                  ))}
                                </Flex>
                              ) : agent.city_port ? (
                                /* Fallback: Parse legacy text data and try to match with city ports */
                                <Flex wrap="wrap" gap={2}>
                                  {getCityPortList(agent.city_port).map((cpName, idx) => {
                                    const matchedCp = findCityPortPIC(cpName);
                                    return (
                                      <Popover.Root key={idx} positioning={{ placement: 'top' }}>
                                        <Popover.Trigger asChild>
                                          <Badge
                                            colorPalette={matchedCp ? 'purple' : 'gray'}
                                            variant="subtle"
                                            borderRadius="full"
                                            px={3}
                                            py={1}
                                            cursor={matchedCp ? 'pointer' : 'default'}
                                            _hover={matchedCp ? { bg: 'purple.100', transform: 'scale(1.05)' } : {}}
                                            transition="all 0.2s"
                                            display="flex"
                                            alignItems="center"
                                            gap={1}
                                          >
                                            <FiMapPin size={12} />
                                            {cpName}
                                          </Badge>
                                        </Popover.Trigger>
                                        {matchedCp && (
                                          <Portal>
                                            <Popover.Positioner>
                                              <Popover.Content bg="white" shadow="xl" borderRadius="lg" p={4} minW="250px">
                                                <Popover.Arrow>
                                                  <Popover.ArrowTip />
                                                </Popover.Arrow>
                                                <VStack align="stretch" spacing={3}>
                                                  <Text fontWeight="bold" color="purple.700" fontSize="md">
                                                    <FiMapPin style={{ display: 'inline', marginRight: '8px' }} />
                                                    {matchedCp.name}
                                                  </Text>
                                                  <Separator />
                                                  <Text fontSize="sm" fontWeight="semibold" color="gray.600">PIC Information</Text>
                                                  {matchedCp.pic_name || matchedCp.pic_email || matchedCp.pic_phone ? (
                                                    <VStack align="stretch" spacing={2}>
                                                      {matchedCp.pic_name && (
                                                        <HStack>
                                                          <FiUser color="gray" />
                                                          <Text fontSize="sm">{matchedCp.pic_name}</Text>
                                                        </HStack>
                                                      )}
                                                      {matchedCp.pic_email && (
                                                        <HStack>
                                                          <FiMail color="gray" />
                                                          <Text fontSize="sm" color="blue.600">{matchedCp.pic_email}</Text>
                                                        </HStack>
                                                      )}
                                                      {matchedCp.pic_phone && (
                                                        <HStack>
                                                          <FiPhone color="gray" />
                                                          <Text fontSize="sm">{matchedCp.pic_phone}</Text>
                                                        </HStack>
                                                      )}
                                                    </VStack>
                                                  ) : (
                                                    <Text fontSize="sm" color="gray.400" fontStyle="italic">No PIC assigned</Text>
                                                  )}
                                                </VStack>
                                              </Popover.Content>
                                            </Popover.Positioner>
                                          </Portal>
                                        )}
                                      </Popover.Root>
                                    );
                                  })}
                                </Flex>
                              ) : (
                                <Text fontSize="sm" color="gray.500">-</Text>
                              )}
                            </Box>

                            <Text fontSize="sm" color="gray.600">Class</Text>
                            <Text fontSize="sm" color="gray.800">{getVal(agent.class)}</Text>

                            <Text fontSize="sm" color="gray.600">Membership Validity</Text>
                            <Text fontSize="sm" color="gray.800">{agent.membership_validity ? formatMembershipDate(agent.membership_validity) : '-'}</Text>
                          </Grid>
                        </Box>
                      </GridItem>

                      <GridItem>
                        <Box border="1px" borderColor="gray.200" borderRadius="lg" p={5} bg="white">
                          <Text fontSize="sm" fontWeight="bold" color="gray.700" mb={3}>System Information</Text>
                          <Grid templateColumns={{ base: '140px 1fr', md: '160px 1fr' }} gap={2}>
                            <Text fontSize="sm" color="gray.600">Date Upload</Text>
                            <Text fontSize="sm" color="gray.800">{agent.date_upload ? formatDate(agent.date_upload) : '-'}</Text>

                            <Text fontSize="sm" color="gray.600">Uploader</Text>
                            <Text fontSize="sm" color="gray.800">{getVal(agent.uploader)}</Text>

                            <Text fontSize="sm" color="gray.600">Last Edited By</Text>
                            <Text fontSize="sm" color="gray.800">{lastEditedBy}</Text>

                            <Text fontSize="sm" color="gray.600">Last Edited Time</Text>
                            <Text fontSize="sm" color="gray.800">{getVal(lastEditedTime)}</Text>
                          </Grid>
                        </Box>
                      </GridItem>
                    </Grid>
                  </VStack>
                </GridItem>

                <GridItem h="full">
                  <Box h="full" bg="gray.50" borderRadius="xl" border="1px" borderColor="gray.200" p={4} position="sticky" top={0}>
                    <AgentComments agentId={agent.id} />
                  </Box>
                </GridItem>
              </Grid>
            </Dialog.Body>

            <Dialog.Footer bg="gray.50" borderBottomRadius="xl">
              <Button colorPalette="purple" onClick={onClose} px={6}>
                Close Details
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Dialog.Root>

      {specialServicesModalOpen && (
        <Dialog.Root open={true} onOpenChange={(e) => !e.open && setSpecialServicesModalOpen(false)}>
          <Dialog.Backdrop bg="blackAlpha.600" backdropFilter="blur(4px)" zIndex={9999} />
          <Dialog.Positioner zIndex={10000}>
            <Dialog.Content maxW="md" borderRadius="xl" boxShadow="2xl" bg="white" p={6}>
              <Flex justify="space-between" align="center" mb={4}>
                <Text fontSize="lg" fontWeight="bold" color="purple.700">Special Services</Text>
                <Button size="sm" variant="ghost" onClick={() => setSpecialServicesModalOpen(false)} aria-label="Close">
                  Close
                </Button>
              </Flex>
              <VStack align="stretch" spacing={2} maxH="60vh" overflowY="auto">
                {specialServices.length === 0 ? (
                  <Text color="gray.500">No special services</Text>
                ) : (
                  specialServices.map((service, idx) => (
                    <HStack key={idx} p={3} bg="purple.50" borderRadius="lg" border="1px solid" borderColor="purple.100" justify="space-between">
                      <HStack>
                        <Badge colorPalette="purple" borderRadius="full" boxSize="24px" display="flex" alignItems="center" justifyContent="center">
                          {idx + 1}
                        </Badge>
                        <Text fontWeight="medium" color="gray.700">{service}</Text>
                      </HStack>
                      <Box as="svg" viewBox="0 0 20 20" fill="green" boxSize={5}>
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </Box>
                    </HStack>
                  ))
                )}
              </VStack>
            </Dialog.Content>
          </Dialog.Positioner>
        </Dialog.Root>
      )}
    </>
  );
};

export default ViewAgentDetailsModal;
